import { cn } from '@solinkcorp/solink-ui';
import React, { useLayoutEffect, useRef, useState } from 'react';
import { carouselOffset } from './feedCarouselOffset';

interface FeedCarouselDotsProps {
  /** How many cameras the widget is rotating through. */
  count: number;
  /** Index of the camera currently on screen. */
  activeIndex: number;
}

/**
 * Carousel dots at the bottom of a live feed, shown only when more than one camera is in the
 * rotation. Decorative: they track the rotation, they don't drive it.
 *
 * While the strip fits, it stays one dot-gap in from each side of the widget. Once it overflows,
 * that gap stays on an end that is still on screen, and a clipped end runs flush to the widget
 * edge. The strip shifts left only when the active dot would otherwise sit past the right edge.
 */
const FeedCarouselDots: React.FC<FeedCarouselDotsProps> = ({ count, activeIndex }) => {
  const viewportRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const activeDotRef = useRef<HTMLSpanElement>(null);
  const [offset, setOffset] = useState(0);

  useLayoutEffect(() => {
    const viewport = viewportRef.current;
    const track = trackRef.current;
    if (!viewport || !track) return;

    const measure = () => {
      const active = activeDotRef.current;
      const trackStyle = getComputedStyle(track);
      const endInset = Number.parseFloat(trackStyle.paddingRight) || 0;
      const sidePadding = Number.parseFloat(trackStyle.columnGap) || 0;
      const next = carouselOffset({
        windowWidth: viewport.clientWidth,
        contentWidth: track.scrollWidth,
        activeStart: active?.offsetLeft ?? 0,
        activeEnd: active ? active.offsetLeft + active.offsetWidth : 0,
        endInset,
        sidePadding,
      });
      setOffset((current) => (current === next ? current : next));
    };

    measure();

    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure);
    observer.observe(viewport);
    return () => observer.disconnect();
  }, [count, activeIndex]);

  if (count < 2) return null;

  return (
    <div
      ref={viewportRef}
      aria-hidden
      data-testid="sdm-live-feed-carousel"
      className="pointer-events-none absolute inset-x-0 bottom-3 z-20 overflow-hidden"
    >
      <div
        ref={trackRef}
        className="relative flex w-max items-center gap-1.5 rounded-full bg-background-surface px-2 py-1.5 transition-transform duration-300"
        style={{ transform: `translateX(${offset}px)` }}
      >
        {Array.from({ length: count }, (_, index) => {
          const active = index === activeIndex;
          return (
            <span
              key={index}
              ref={active ? activeDotRef : undefined}
              data-testid="sdm-live-feed-carousel-dot"
              data-active={active || undefined}
              className={cn(
                'shrink-0 rounded-full transition-[width,height,background-color] duration-300',
                active ? 'h-2 w-2 bg-primary' : 'h-1.5 w-1.5 bg-border-muted',
              )}
            />
          );
        })}
      </div>
    </div>
  );
};

export default React.memo(FeedCarouselDots);
